import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import HeaderBar from "../../components/showcase/HeaderBar";
import HealthPill from "../../components/showcase/HealthPill";
import showcaseApi from "../../lib/showcaseApi";

const BUCKET_META = [
  { key: "current_status", title: "How this product is doing right now" },
  { key: "work_in_progress", title: "What we are working on" },
  { key: "waiting_on_owner", title: "Waiting on the owner" },
  { key: "waiting_on_others", title: "Waiting on someone or something else" },
  { key: "recent_milestones", title: "What we’ve built so far" },
];

function ContentBlock({ title, body, testid, swatch }) {
  if (!body || body.trim().length < 3) return null;
  return (
    <section className="showcase-card product-identity-section p-5 sm:p-6" style={{ "--product-swatch": swatch }} data-testid={testid}>
      <h3
        className="font-heading text-lg sm:text-xl mb-3"
        style={{ color: "#1F1F1F", fontWeight: 600 }}
      >
        {title}
      </h3>
      <p className="text-sm sm:text-base leading-relaxed text-foreground whitespace-pre-line max-w-prose">
        {body}
      </p>
    </section>
  );
}

export default function ProductDrilldownPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const r = await showcaseApi.product(slug);
      setData(r);
    } catch (err) {
      if (err.status === 401) {
        navigate("/showcase/login", { replace: true });
        return;
      }
      if (err.status === 404) {
        setError("We don’t recognise that product.");
      } else {
        setError(err.message || "We couldn’t fetch this product’s details right now.");
      }
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    try {
      await showcaseApi.logout();
    } catch (e) {}
    navigate("/showcase/login", { replace: true });
  };

  const product = data?.product;

  return (
    <div
      className="min-h-screen w-full"
      style={{ background: "hsl(var(--background))" }}
      data-testid="product-drilldown-page"
    >
      <HeaderBar
        lastUpdated={data?.last_updated}
        onLogout={handleLogout}
        onRefresh={load}
        subtitle="A closer look at one of our products."
      />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-4">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="gap-1.5 pl-1"
            data-testid="product-back-button"
          >
            <Link to="/showcase">
              <ArrowLeft size={14} strokeWidth={2.25} />
              Back to all products
            </Link>
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="product-error-alert" className="mb-6">
            <AlertTitle>We ran into a problem</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        )}

        {!loading && product && (
          <div className="space-y-6">
            {/* Hero */}
            <div className="showcase-card product-identity-surface p-6 sm:p-8" style={{ "--product-swatch": product.swatch }} data-testid="product-page-title">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <p className="eyebrow flex items-center gap-2 text-muted-foreground" data-testid="product-page-identity">
                    <span className="product-identity-dot" aria-hidden="true" />LiftTech Product
                  </p>
                  <h1
                    className="font-heading text-3xl sm:text-4xl mt-1"
                    style={{ color: "#1F1F1F", fontWeight: 600 }}
                  >
                    {product.display_name}
                  </h1>
                  {product.tagline && (
                    <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-prose break-words [overflow-wrap:anywhere]" data-testid="product-page-tagline">
                      {product.tagline}
                    </p>
                  )}
                </div>
                <div data-testid="product-page-health-pill">
                  <HealthPill health={product.health} size="lg" />
                </div>
              </div>
              {product.one_line_status && (
                <p className="text-base leading-relaxed text-foreground mt-4 max-w-prose">
                  <span className="font-medium">Right now: </span>
                  {product.one_line_status}
                </p>
              )}
              {product.prod_state && (
                <p className="text-sm text-muted-foreground mt-1 max-w-prose">
                  <span className="font-medium">Where it lives: </span>
                  {product.prod_state}
                </p>
              )}
            </div>

            {product.content_notice && <p className="text-sm text-muted-foreground" data-testid="product-page-content-notice">{product.content_notice}</p>}
            {BUCKET_META.map((b) => (
              <ContentBlock
                key={b.key}
                title={b.title}
                body={product[b.key]}
                swatch={product.swatch}
                testid={`product-page-${b.key.replace(/_/g, "-")}`}
              />
            ))}

            {/* Fallback if all buckets empty */}
            {BUCKET_META.every(
              (b) => !product[b.key] || product[b.key].trim().length < 3
            ) && (
              <div
                className="showcase-card p-6 text-sm text-muted-foreground"
                data-testid="product-page-empty"
              >
                We don’t have deeper detail on this product yet. Check back after the
                team’s next update.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
